import { TextField } from '@mui/material'
import { forwardRef, ForwardedRef } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '~/app/hooks'
import { currentTheme } from '~/redux/features'
import { ALL_NS } from '~/i18n/namespaces'
import { EFInputLabel } from '../EFInputLabel'
import { EFTextFieldProps } from './EFTextField.model'
import classNames from 'classnames/bind'
import styles from './EFTextField.module.scss'
import { CustomTextField } from './CustomTextField'

const cx = classNames.bind(styles)

type NumberValidity = 'valid' | 'notANumber' | 'notAnInteger'

function getNumberValidity(value: unknown, allowDecimal: boolean): NumberValidity {
  if (value === undefined || value === null || value === '') return 'valid' // required-ness is handled separately
  if (typeof value !== 'string' && typeof value !== 'number') return 'notANumber'
  const strValue = `${value}`
  if (!/^-?\d+(\.\d+)?$/.test(strValue)) return 'notANumber'
  if (!allowDecimal && strValue.includes('.')) return 'notAnInteger'
  return 'valid'
}

export const EFNumericField = forwardRef(
  (props: EFTextFieldProps, _ref: ForwardedRef<unknown>) => {
    const { control, setValue } = useFormContext() ?? {}
    const theme = useAppSelector(currentTheme)
    const { t } = useTranslation(ALL_NS)
    const {validateFn, blurFn, allowDecimal, ...baseProps} = props; //Some of these props shouldnt be passed down
    const defaultValidateFn = (value: unknown) => {
      switch (getNumberValidity(value, allowDecimal ?? false)) {
        case 'notANumber':
          return t('This field must be number')
        case 'notAnInteger':
          return t('This field must be integer')
        default:
          return undefined
      }
    }

    if (!control)
      return (
        <CustomTextField
          type='text'
          {...props}
        />
      )
    return (
      <Controller
        control={control}
        name={props.name}
        rules={{ validate: props.validateFn ?? defaultValidateFn }}
        render={({ field, fieldState }) => (
          <div className='custom-field'>
            {props.label && (
              <EFInputLabel
                label={props.label}
                name={props.name}
                sx={props.labelSx}
              />
            )}
            <TextField
              {...baseProps}
              {...field}
              autoComplete='off'
              type='text'
              name={field.name}
              value={field.value}
              placeholder={props.placeholder}
              id={field.name}
              InputLabelProps={{ sx: { display: 'none' }, shrink: false }}
              className={cx('text-field', theme)}
              error={props.error ?? fieldState.invalid}
              helperText={props.helperText ?? fieldState.error?.message}
              FormHelperTextProps={{
                sx: { marginX: 0, color: 'red !important' }
              }}
              onChange={(e: any) => {
                e.target.value = e.target.value.replace(allowDecimal ? /[^\d.-]/g : /[^\d-]/g, '')
                setValue(field.name, e.target.value, {
                  shouldValidate: true,
                  shouldDirty: true
                })
                props.onChange?.(e)
              }}
              onBlur={(e: any) => {
                //Use this to remove any leading zeroes from the value, it is ultimately resolved as a number but this tidies the display value
                const val = Number.parseFloat(e.target.value)
                if (!Number.isNaN(val) && val.toString() !== e.target.value) {
                  setValue(field.name, val.toString(), {
                    shouldValidate: true,
                    shouldDirty: true
                  })
                }
                props.blurFn?.(e);
              }}
              inputProps={{
                inputMode: allowDecimal ? 'decimal' : 'numeric'
              }}
            />
          </div>
        )}
      />
    )
  }
)
